import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import type { AgentBlock, Id, Mode, WsAgentBlock, WsAgentDelta, WsAgentTool, WsAgentTurnEnded, WsAgentTurnStarted } from "../api/types";

/*
  The words of a turn as they arrive over the live channel (2.10). They go to the person who owns the
  conversation only. The stored conversation is read again when the turn ends, and then the entry here
  is dropped, so the screen never shows the same words twice.
*/

export type StreamPart =
  | { seq: number; kind: "text"; text: string }
  | { seq: number; kind: "block"; block: AgentBlock }
  | { seq: number; kind: "tool"; name: string; status: "running" | "done" | "refused"; summary: string };

export interface TurnState {
  turnId: Id;
  /** Null when the turn was joined in the middle (the start was missed) and not matched yet. */
  conversationKey: string | null;
  mode: Mode | null;
  /** In the order of `seq`. */
  parts: StreamPart[];
  status: "running" | "ended";
  /** A turn joined in the middle: the live messages up to this `seq` are in the stored reply already (`RunningTurn.last_seq`). */
  skipUpTo?: number;
  endedReason?: string;
  endedMessage?: string | null;
}

export interface TurnEnd {
  turnId: Id;
  reason: string;
  message: string | null;
}

interface AgentData {
  turns: Record<Id, TurnState>;
  /** Insertion order of the turns, oldest first, for pruning. */
  order: Id[];
  /** How the last turn of each conversation ended, when it did not end well. Cleared by the next message. */
  lastEnd: Record<string, TurnEnd>;
}

interface AgentActions {
  reset(): void;
  start(message: WsAgentTurnStarted): void;
  delta(message: WsAgentDelta): void;
  block(message: WsAgentBlock): void;
  tool(message: WsAgentTool): void;
  end(message: WsAgentTurnEnded): void;
  /** Join a turn that is already running (the page was opened mid-turn). */
  adopt(turnId: Id, conversationKey: string, textSoFar: string, lastSeq?: number): void;
  dismiss(turnId: Id): void;
  clearEnd(conversationKey: string): void;
}

/** How many finished turns are kept when no screen has taken them off. */
const KEEP_ENDED = 10;

function blank(turnId: Id): TurnState {
  return { turnId, conversationKey: null, mode: null, parts: [], status: "running" };
}

function insertPart(parts: StreamPart[], part: StreamPart, skipUpTo = 0): StreamPart[] {
  // A part the stored reply holds already (a turn joined in the middle) is not shown twice.
  if (skipUpTo > 0 && part.seq <= skipUpTo) return parts;
  // A part with a seq that is already here is the same part again: the first one wins.
  if (parts.some((p) => p.seq === part.seq && p.kind === part.kind)) return parts;
  const next = [...parts, part];
  next.sort((a, b) => a.seq - b.seq);
  return next;
}

const initial: AgentData = { turns: {}, order: [], lastEnd: {} };

export const useAgentStore = create<AgentData & AgentActions>((set) => {
  const touch = (state: AgentData, turnId: Id, change: (turn: TurnState) => TurnState): Pick<AgentData, "turns" | "order"> => {
    const existing = state.turns[turnId] ?? blank(turnId);
    const order = state.order.includes(turnId) ? state.order : [...state.order, turnId];
    return { turns: { ...state.turns, [turnId]: change(existing) }, order };
  };

  return {
    ...initial,
    reset: () => set({ turns: {}, order: [], lastEnd: {} }),
    start: (m) =>
      set((state) =>
        touch(state, m.turn_id, (turn) => ({ ...turn, conversationKey: m.conversation_key, mode: m.mode, status: "running" })),
      ),
    delta: (m) =>
      set((state) => touch(state, m.turn_id, (turn) => ({ ...turn, parts: insertPart(turn.parts, { seq: m.seq, kind: "text", text: m.text }, turn.skipUpTo) }))),
    block: (m) =>
      set((state) => touch(state, m.turn_id, (turn) => ({ ...turn, parts: insertPart(turn.parts, { seq: m.seq, kind: "block", block: m.block }, turn.skipUpTo) }))),
    tool: (m) =>
      set((state) =>
        touch(state, m.turn_id, (turn) => {
          // "running" and then "done" for the same call: the later word replaces the earlier one.
          const index = turn.parts.findIndex((p) => p.kind === "tool" && p.name === m.name && (p.seq === m.seq || p.status === "running"));
          const part: StreamPart = { seq: m.seq, kind: "tool", name: m.name, status: m.status, summary: m.summary };
          if (index >= 0) {
            const parts = [...turn.parts];
            const old = parts[index]!;
            parts[index] = { ...part, seq: old.seq };
            return { ...turn, parts };
          }
          return { ...turn, parts: insertPart(turn.parts, part, turn.skipUpTo) };
        }),
      ),
    end: (m) =>
      set((state) => {
        const changed = touch(state, m.turn_id, (turn) => ({ ...turn, status: "ended", endedReason: m.reason, endedMessage: m.message ?? null }));
        const turn = changed.turns[m.turn_id]!;
        const lastEnd = { ...state.lastEnd };
        if (turn.conversationKey && (m.reason !== "done" || m.message)) {
          lastEnd[turn.conversationKey] = { turnId: m.turn_id, reason: m.reason, message: m.message ?? null };
        }
        // Keep the finished turns that no screen took off, but not for ever.
        const ended = changed.order.filter((id) => changed.turns[id]?.status === "ended");
        const drop = ended.slice(0, Math.max(0, ended.length - KEEP_ENDED));
        if (drop.length === 0) return { ...changed, lastEnd };
        const turns = { ...changed.turns };
        for (const id of drop) delete turns[id];
        return { turns, order: changed.order.filter((id) => !drop.includes(id)), lastEnd };
      }),
    adopt: (turnId, conversationKey, textSoFar, lastSeq = 0) =>
      set((state) => {
        const existing = state.turns[turnId];
        if (existing) {
          if (existing.conversationKey) return state;
          if (lastSeq > 0) {
            // live messages came before the stored reply was read: those it holds already go, its words come first
            const kept = existing.parts.filter((p) => p.seq > lastSeq);
            return touch(state, turnId, (turn) => ({
              ...turn,
              conversationKey,
              skipUpTo: lastSeq,
              parts: textSoFar ? [{ seq: -1, kind: "text", text: textSoFar }, ...kept] : kept,
            }));
          }
          return touch(state, turnId, (turn) => ({ ...turn, conversationKey }));
        }
        // The words seen so far are the first part. The next delta continues them.
        return touch(state, turnId, (turn) => ({
          ...turn,
          conversationKey,
          skipUpTo: lastSeq > 0 ? lastSeq : undefined,
          parts: textSoFar ? [{ seq: -1, kind: "text", text: textSoFar }] : [],
        }));
      }),
    dismiss: (turnId) =>
      set((state) => {
        if (!state.turns[turnId]) return state;
        const turns = { ...state.turns };
        delete turns[turnId];
        return { turns, order: state.order.filter((id) => id !== turnId) };
      }),
    clearEnd: (conversationKey) =>
      set((state) => {
        if (!state.lastEnd[conversationKey]) return state;
        const lastEnd = { ...state.lastEnd };
        delete lastEnd[conversationKey];
        return { lastEnd };
      }),
  };
});

/** The turns of one conversation, oldest first. Turns whose start was missed are included when `includeUnmatched` is set. */
export function useTurnsFor(conversationKey: string): TurnState[] {
  return useAgentStore(
    useShallow((state) =>
      state.order
        .map((id) => state.turns[id])
        .filter((turn): turn is TurnState => !!turn && turn.conversationKey === conversationKey),
    ),
  );
}

/** The text of a turn: its text parts put together. */
export function textOf(turn: TurnState): string {
  return turn.parts
    .filter((p): p is Extract<StreamPart, { kind: "text" }> => p.kind === "text")
    .map((p) => p.text)
    .join("");
}
